"use client";

/**
 * First-touch signup attribution capture.
 *
 * Runs once on mount on the marketing site (superpowerresume.com). If we have
 * never recorded attribution for this visitor (no `spr_attr` cookie), and this
 * looks like an acquisition event — the URL carries a utm_source, OR the
 * referrer is an external site — we stamp a 30-day cookie with the first-touch
 * details.
 *
 * The cookie is scoped to `domain=.superpowerresume.com` so it is also
 * readable from app.superpowerresume.com, where the signup routes parse it
 * (parent-domain cookies are shared across subdomains).
 *
 * First-touch: once the cookie exists we never overwrite it, so the very first
 * campaign/referrer that brought the visitor in is the one credited at signup.
 *
 * Everything is wrapped in try/catch — attribution is best-effort and must
 * never affect the page.
 */
import { useEffect } from "react";

const COOKIE_NAME = "spr_attr";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
const COOKIE_DOMAIN = ".superpowerresume.com";

function hasAttributionCookie(): boolean {
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${COOKIE_NAME}=`));
}

function isInternalReferrer(referrer: string): boolean {
  if (!referrer) return true; // no referrer -> treat as internal/direct
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host === "superpowerresume.com" || host.endsWith(".superpowerresume.com");
  } catch {
    return true;
  }
}

export default function AttributionCapture() {
  useEffect(() => {
    try {
      if (hasAttributionCookie()) return; // first-touch already recorded

      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get("utm_source");
      const referrer = document.referrer || "";
      const referrerIsExternal = !isInternalReferrer(referrer);

      // Only capture on an actual acquisition signal.
      if (!utmSource && !referrerIsExternal) return;

      const attribution = {
        utm_source: utmSource || null,
        utm_medium: params.get("utm_medium") || null,
        utm_campaign: params.get("utm_campaign") || null,
        utm_content: params.get("utm_content") || null,
        referrer: referrer || null,
        landing_page: window.location.href,
        captured_at: new Date().toISOString(),
      };

      const value = encodeURIComponent(JSON.stringify(attribution));
      document.cookie =
        `${COOKIE_NAME}=${value};` +
        `max-age=${MAX_AGE_SECONDS};` +
        `path=/;` +
        `domain=${COOKIE_DOMAIN};` +
        `SameSite=Lax;` +
        `Secure`;
    } catch {
      // best-effort only
    }
  }, []);

  return null;
}
